import { FormEvent, memo, TransitionEvent, useCallback } from "react"

import { CheckboxNew } from "components/form/CheckboxNew"
import { InputLogo } from "components/form/InputLogo"
import { InputNew } from "components/form/InputNew"
import { InputPhone } from "components/form/InputPhone"

import type { PhoneErrors, UrlErrors } from "./partnersFormUtils"
import {
  AccordionBody,
  AccordionInner,
  AccordionTrigger,
  CheckboxContainer,
  CheckboxLabel,
  CheckboxSlot,
  ChevronWrap,
  Row,
  RowContent,
  RowHeader,
  RowLabel,
  TwoColumns,
} from "./styled"
import type { PartnerValues } from "./types"

const CHEVRON_ICON = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export type PartnerCategoryRowProps = {
  rowKey: string
  categoryName: string
  isChecked: boolean
  showAccordionBody: boolean
  isAccordionOpen: boolean
  values: PartnerValues
  phoneErrors: PhoneErrors
  urlErrors: UrlErrors
  logoError?: string
  logoImageUrl?: string
  onCheckedChange: (key: string, checked: boolean) => void
  onToggleAccordion: (key: string) => void
  onAccordionTransitionEnd: (e: TransitionEvent<HTMLDivElement>, key: string) => void
  onPhoneInput: (key: string, value: string) => void
  onPhoneForTextsInput: (key: string, value: string) => void
  onUseForTextsChange: (key: string, checked: boolean) => void
  onWebsiteUrlInput: (key: string, value: string) => void
  onScheduleUrlInput: (key: string, value: string) => void
  onPhoneBlur: (key: string, field: "phone" | "phoneForTexts") => void
  onUrlBlur: (key: string, field: "websiteUrl" | "scheduleUrl") => (e: FormEvent<HTMLInputElement>) => void
  onLogoSetFile: (key: string, files: FileList | null) => void
  onLogoValidationError: (key: string, message: string) => void
}

const PartnerCategoryRowInner = ({
  rowKey,
  categoryName,
  isChecked,
  showAccordionBody,
  isAccordionOpen,
  values,
  phoneErrors,
  urlErrors,
  logoError,
  logoImageUrl,
  onCheckedChange,
  onToggleAccordion,
  onAccordionTransitionEnd,
  onPhoneInput,
  onPhoneForTextsInput,
  onUseForTextsChange,
  onWebsiteUrlInput,
  onScheduleUrlInput,
  onPhoneBlur,
  onUrlBlur,
  onLogoSetFile,
  onLogoValidationError,
}: PartnerCategoryRowProps) => {
  const handleCheckedChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onCheckedChange(rowKey, e.target.checked),
    [rowKey, onCheckedChange],
  )
  const handleToggle = useCallback(() => isChecked && onToggleAccordion(rowKey), [rowKey, isChecked, onToggleAccordion])
  const handleTransitionEnd = useCallback(
    (e: TransitionEvent<HTMLDivElement>) => onAccordionTransitionEnd(e, rowKey),
    [rowKey, onAccordionTransitionEnd],
  )
  const handlePhoneInput = useCallback((val: string) => onPhoneInput(rowKey, val), [rowKey, onPhoneInput])
  const handlePhoneForTextsInput = useCallback(
    (val: string) => onPhoneForTextsInput(rowKey, val),
    [rowKey, onPhoneForTextsInput],
  )
  const handleUseForTextsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onUseForTextsChange(rowKey, e.target.checked),
    [rowKey, onUseForTextsChange],
  )
  const handleWebsiteUrlInput = useCallback(
    (e: FormEvent<HTMLInputElement>) => onWebsiteUrlInput(rowKey, e.currentTarget.value),
    [rowKey, onWebsiteUrlInput],
  )
  const handleScheduleUrlInput = useCallback(
    (e: FormEvent<HTMLInputElement>) => onScheduleUrlInput(rowKey, e.currentTarget.value),
    [rowKey, onScheduleUrlInput],
  )
  const handlePhoneBlur = useCallback(() => onPhoneBlur(rowKey, "phone"), [rowKey, onPhoneBlur])
  const handlePhoneForTextsBlur = useCallback(() => onPhoneBlur(rowKey, "phoneForTexts"), [rowKey, onPhoneBlur])
  const handleWebsiteUrlBlur = useCallback(
    (e: FormEvent<HTMLInputElement>) => onUrlBlur(rowKey, "websiteUrl")(e),
    [rowKey, onUrlBlur],
  )
  const handleScheduleUrlBlur = useCallback(
    (e: FormEvent<HTMLInputElement>) => onUrlBlur(rowKey, "scheduleUrl")(e),
    [rowKey, onUrlBlur],
  )
  const handleLogoSetFile = useCallback(
    (files: FileList | null) => onLogoSetFile(rowKey, files),
    [rowKey, onLogoSetFile],
  )
  const handleLogoError = useCallback(
    (message: string) => onLogoValidationError(rowKey, message),
    [rowKey, onLogoValidationError],
  )

  return (
    <Row id={`partner-row-${rowKey}`} data-testid={`partner-row-${rowKey}`}>
      <RowHeader>
        <CheckboxSlot data-testid={`partner-checkbox-${rowKey}`} onClick={e => e.stopPropagation()}>
          <CheckboxNew id={`checkbox-${rowKey}`} isChecked={isChecked} onChange={handleCheckedChange} name={rowKey} />
        </CheckboxSlot>
        <AccordionTrigger
          type="button"
          data-testid={`partner-trigger-${rowKey}`}
          $disabled={!isChecked}
          tabIndex={isChecked ? 0 : -1}
          aria-expanded={isChecked ? isAccordionOpen : undefined}
          aria-disabled={!isChecked}
          onClick={handleToggle}
        >
          <RowLabel>{categoryName}</RowLabel>
          <ChevronWrap $open={isChecked && isAccordionOpen} $placeholder={!isChecked} aria-hidden={!isChecked}>
            {CHEVRON_ICON}
          </ChevronWrap>
        </AccordionTrigger>
      </RowHeader>

      {showAccordionBody && (
        <AccordionBody $open={isAccordionOpen} onTransitionEnd={handleTransitionEnd}>
          <AccordionInner>
            <RowContent>
              <div>
                <TwoColumns>
                  <InputPhone
                    label="Phone number"
                    placeholder="403 377 2000"
                    value={values.phone}
                    onChange={handlePhoneInput}
                    onBlur={handlePhoneBlur}
                    errorMessage={phoneErrors.phone}
                  />
                  {!values.useForTexts && (
                    <InputPhone
                      label="Phone number for text messages"
                      placeholder="403 377 2000"
                      value={values.phoneForTexts}
                      onChange={handlePhoneForTextsInput}
                      onBlur={handlePhoneForTextsBlur}
                      errorMessage={phoneErrors.phoneForTexts}
                    />
                  )}
                </TwoColumns>
                <CheckboxContainer>
                  <CheckboxNew id={`texts-${rowKey}`} isChecked={values.useForTexts} onChange={handleUseForTextsChange}>
                    <CheckboxLabel>Use this number to get text messages</CheckboxLabel>
                  </CheckboxNew>
                </CheckboxContainer>
              </div>

              <TwoColumns>
                <InputNew
                  label="Website URL"
                  placeholder="https://..."
                  value={values.websiteUrl}
                  onChange={handleWebsiteUrlInput}
                  onBlur={handleWebsiteUrlBlur}
                  errorMessage={urlErrors.websiteUrl}
                />
                <InputNew
                  label="Schedule URL"
                  placeholder="https://..."
                  value={values.scheduleUrl}
                  onChange={handleScheduleUrlInput}
                  onBlur={handleScheduleUrlBlur}
                  errorMessage={urlErrors.scheduleUrl}
                />
              </TwoColumns>

              <InputLogo
                id={`logo-${rowKey}`}
                name={`logo-${rowKey}`}
                label="Partner's logo (optional)"
                formatNote="(Format: Png Ratio: 2:1 Max size: 750x375px)"
                imageUrl={logoImageUrl}
                value={values.logo ? [values.logo] : null}
                errorMessage={logoError}
                onSetFile={handleLogoSetFile}
                onValidationError={handleLogoError}
              />
            </RowContent>
          </AccordionInner>
        </AccordionBody>
      )}
    </Row>
  )
}

export const PartnerCategoryRow = memo(PartnerCategoryRowInner)
